import { useState } from "react";
import { PrioritySearch, PrioritySearchMatch } from "@therapprove/common";

export type GlobalReferralFilterState = {
    distanceMiles: number | undefined;
    ageRange: string | undefined;
    therapyCategoryIds: string[];
    conditionIds: string[];
    daysAvailable: string[];
    timesAvailable: string[];
};

export const defaultGlobalReferralFilters: GlobalReferralFilterState = {
    distanceMiles: undefined,
    ageRange: undefined,
    therapyCategoryIds: [],
    conditionIds: [],
    daysAvailable: [],
    timesAvailable: []
};

// Parse age range string (e.g. "6-12") into older_than/younger_than values
const parseAgeRange = (range: string) => {
    if (range === "18+") {
        return { older_than: 18 };
    }
    const [min, max] = range.split("-").map(Number);
    return { older_than: min, younger_than: max };
};

/**
 * Builds a PrioritySearch.where object from the filter state.
 * The base global filter (is_global, archived exclusion, location)
 * is merged by the caller — this only produces the user-selected filters.
 */
export const buildFilterWhere = (
    filters: GlobalReferralFilterState,
    locationPostalCodes: string[]
) => {
    const where: PrioritySearch["where"] = {};

    if (filters.ageRange) {
        where.age_years = parseAgeRange(filters.ageRange);
    }

    if (filters.conditionIds.length) {
        where.condition_ids = {
            ids: filters.conditionIds,
            match: PrioritySearchMatch.Any
        };
    }

    if (filters.therapyCategoryIds.length) {
        where.therapy_category_ids = {
            ids: filters.therapyCategoryIds,
            match: PrioritySearchMatch.Any
        };
    }

    if (filters.daysAvailable.length) {
        where.days_available = {
            names: filters.daysAvailable,
            match: PrioritySearchMatch.Any
        };
    }

    if (filters.timesAvailable.length) {
        where.times_available = {
            names: filters.timesAvailable,
            match: PrioritySearchMatch.Any
        };
    }

    if (filters.distanceMiles) {
        where.location = {
            distance_in_miles: filters.distanceMiles,
            postal_codes: locationPostalCodes
        };
    }

    return where;
};

export const useGlobalReferralFilters = () => {
    const [filters, setFilters] = useState<GlobalReferralFilterState>(
        defaultGlobalReferralFilters
    );

    return { filters, setFilters };
};
